<template>
  <div class="user-section">
    <div v-if="currentUser" class="user-dropdown">
      <span
        class="user-email"
        tabindex="0"
        @click="toggleUserMenu"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
        @focus="handleFocus"
        @blur="handleBlur"
      >
        {{ currentProfileName }}
        <span v-if="currentUser.email" class="user-email-secondary"
          >({{ currentUser.email }})</span
        >
        <span class="dropdown-arrow">▼</span>
      </span>
      <div
        v-if="shouldShowMenu"
        class="user-menu"
        @mouseenter="handleMenuMouseEnter"
        @mouseleave="handleMenuMouseLeave"
      >
        <!-- Custom menu items passed from parent -->
        <div
          v-for="item in menuItems"
          :key="item.id"
          @click="item.action"
          class="menu-item"
          :class="item.class"
        >
          {{ item.label }}
        </div>

        <!-- Separator between custom items and common items -->
        <div
          v-if="menuItems && menuItems.length > 0"
          class="menu-separator"
        ></div>

        <!-- Profile switching option -->
        <div @click="handleProfileSwitch" class="menu-item common">
          Vaihda profiilia
        </div>

        <!-- Logout always at the bottom -->
        <div @click="handleLogout" class="menu-item common">Kirjaudu ulos</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";

interface MenuItem {
  id: string;
  label: string;
  action: () => void;
  class?: string;
}

defineProps<{
  menuItems?: MenuItem[];
}>();

const authStore = useAuthStore();
const router = useRouter();

const currentUser = computed(() => authStore.user);
const currentProfileName = computed(() => authStore.currentProfileName);

// Menu state management
const isMenuOpen = ref(false);
const isHovered = ref(false);
const isFocused = ref(false);
const isMenuHovered = ref(false);

const shouldShowMenu = computed(
  () =>
    isMenuOpen.value ||
    isHovered.value ||
    isFocused.value ||
    isMenuHovered.value,
);

const toggleUserMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
};

const handleMouseEnter = () => {
  isHovered.value = true;
};

const handleMouseLeave = () => {
  isHovered.value = false;
};

const handleFocus = () => {
  isFocused.value = true;
};

const handleBlur = () => {
  isFocused.value = false;
  isMenuOpen.value = false;
};

const handleMenuMouseEnter = () => {
  isMenuHovered.value = true;
};

const handleMenuMouseLeave = () => {
  isMenuHovered.value = false;
};

const handleProfileSwitch = async () => {
  await router.push({ name: "profile-selection" });
  isMenuOpen.value = false;
};

const handleLogout = async () => {
  await authStore.logout();
  await router.push({ name: "login" });
  isMenuOpen.value = false;
};
</script>

<style scoped>
.user-section {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-weight: 600;
}

.user-dropdown {
  position: relative;
}

.user-email {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--radius-sm);
  transition: all 0.2s ease-in-out;
  line-height: 1.2;
}

.user-email:hover {
  background-color: var(--color-bg-tertiary);
  color: var(--color-text-primary);
}

.user-email-secondary {
  font-size: 0.75rem;
  color: var(--color-text-light);
  font-weight: 400;
  margin-left: var(--space-sm);
}

.dropdown-arrow {
  font-size: 0.7rem;
  margin-left: var(--space-xs);
  transition: transform 0.2s ease-in-out;
}

.user-menu {
  position: absolute;
  top: calc(100% + var(--space-xs));
  right: 0;
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  min-width: 200px;
  z-index: 1000;
  overflow: hidden;
}

.menu-item {
  padding: var(--space-sm) var(--space-md);
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  font-size: 0.875rem;
  color: var(--color-text-primary);
}

.menu-item:last-child {
  border-bottom: none;
}

.menu-item:hover {
  background-color: var(--color-bg-secondary);
}

.common {
  color: var(--color-primary);
}

.common:hover {
  background-color: var(--color-bg-tertiary);
}

.menu-separator {
  height: 1px;
  background-color: var(--color-border-light);
  margin: var(--space-xs) 0;
}
</style>
